/**
 * BCP Location Integration Layer
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { Logger } from '@/lib/app-log.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class BcpLocationIntegration {
    constructor(bcpService) {
        this.service = bcpService;
    }

    /**
     * Fetch related locations for a BCP
     */
    async getRelated(bcpId, params = {}) {
        try {
            const response = await this.service.getRelatedLocations(bcpId, params);
            return response;
        } catch (error) {
            Logger.error("Failed to fetch BCP locations:", { error: error?.message });
            return { success: false, data: { items: [], pagination: {} } };
        }
    }

    /**
     * Link locations to a BCP
     */
    async link(bcpId, locations, opts = {}) {
        try {
            const response = await this.service.addLocations(bcpId, locations);
            if (response.success && !opts.silent) {
                toast.success(i18next.t('bcmCommon:messages.createSuccess', { defaultValue: 'Locations linked successfully' }));
            }
            return response;
        } catch (error) {
            Logger.error("Failed to link BCP location:", { error: error?.message });
            if (!opts.silent) toast.error(getIntegrationErrorMessage(error));
            throw error;
        }
    }

    /**
     * Update a location relation for a BCP
     */
    async update(bcpId, locationId, data, opts = {}) {
        try {
            const response = await this.service.updateLocation(bcpId, locationId, data);
            if (response.success && !opts.silent) {
                toast.success(i18next.t('bcmCommon:messages.updateSuccess', { defaultValue: 'Successfully updated' }));
            }
            return response;
        } catch (error) {
            Logger.error("Failed to update BCP location:", { error: error?.message });
            if (!opts.silent) toast.error(getIntegrationErrorMessage(error));
            throw error;
        }
    }

    /**
     * Unlink a location from a BCP
     */
    async unlink(bcpId, locationId, opts = {}) {
        try {
            const response = await this.service.removeLocation(bcpId, locationId);
            if (response.success && !opts.silent) {
                toast.success(i18next.t('bcmCommon:messages.unlinkSuccess', { defaultValue: 'Unlinked successfully' }));
            }
            return response;
        } catch (error) {
            Logger.error("Failed to unlink BCP location:", { error: error?.message });
            if (!opts.silent) toast.error(getIntegrationErrorMessage(error));
            throw error;
        }
    }

    /**
     * Bulk unlink locations from a BCP. Single summary toast.
     */
    async unlinkBulk(bcpId, ids) {
        try {
            const cleanIds = Array.isArray(ids) ? ids.filter(Boolean) : [];
            for (const id of cleanIds) {
                await this.service.removeLocation(bcpId, id);
            }
            toast.success(i18next.t('bcmCommon:messages.unlinkSuccess', { defaultValue: 'Unlinked successfully' }));
            return { success: true, count: cleanIds.length };
        } catch (error) {
            Logger.error("Failed to bulk unlink BCP locations:", { error: error?.message });
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: error.message };
        }
    }
}
